import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { api, ApiError } from '../lib/api';
import { useTurnstile } from '../lib/useTurnstile';
import { AuthShell } from '../components/AuthShell';

const forgotSchema = z.object({
  email: z.string().email('Enter a valid email'),
});

type ForgotForm = z.infer<typeof forgotSchema>;

export const Route = createFileRoute('/forgot')({
  component: ForgotPage,
});

function ForgotPage() {
  const [serverError, setServerError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const { ref: turnstileRef, token: turnstileToken, reset: resetTurnstile } =
    useTurnstile();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotForm>({ resolver: zodResolver(forgotSchema) });

  const onSubmit = async (values: ForgotForm) => {
    setServerError(null);
    if (!turnstileToken) {
      setServerError('Please complete the bot check above.');
      return;
    }
    try {
      await api.requestReset({ email: values.email, turnstileToken });
      setSubmitted(true);
    } catch (err) {
      resetTurnstile();
      // Do not leak whether the email exists. Surface only generic errors
      // (e.g. rate limit, failed challenge).
      if (err instanceof ApiError) {
        setServerError(err.message);
      } else {
        setServerError('Something went wrong. Please try again.');
      }
    }
  };

  if (submitted) {
    return (
      <AuthShell tagline="Check your inbox." taglineSub="We sent you a link if your email is registered.">
        <h1 className="font-display text-3xl font-bold tracking-tight">
          Check your email
        </h1>
        <p className="mt-3 text-sm text-[color:var(--color-text-muted)]">
          If an account exists for that email, we’ve sent a reset link. Check your
          inbox (and spam) in the next few minutes.
        </p>
        <div className="mt-8 space-y-2">
          <Link
            to="/login"
            className="inline-flex items-center text-sm font-semibold text-gold-700 underline"
          >
            Back to sign in
          </Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell tagline="Forgotten your password?" taglineSub="We’ll email you a reset link.">
      <h1 className="font-display text-3xl font-bold tracking-tight">Reset your password</h1>
      <p className="mt-2 text-sm text-[color:var(--color-text-muted)]">
        Enter the email on your Babul Ilm account. If it’s registered we’ll send a link
        to reset your password.
      </p>

      <form onSubmit={handleSubmit(onSubmit)} className="mt-8 space-y-5" noValidate>
        <div>
          <label htmlFor="email" className="block text-sm font-medium">
            Email
          </label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            {...register('email')}
            className="mt-1 w-full rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] px-3 py-2"
            aria-invalid={errors.email ? 'true' : undefined}
            aria-describedby={errors.email ? 'email-error' : undefined}
          />
          {errors.email && (
            <p id="email-error" className="mt-1 text-sm text-danger">
              {errors.email.message}
            </p>
          )}
        </div>

        <div ref={turnstileRef} role="group" aria-label="Bot check" />

        {serverError && (
          <p role="alert" className="text-sm text-danger">
            {serverError}
          </p>
        )}

        <button
          type="submit"
          disabled={isSubmitting || !turnstileToken}
          className="w-full inline-flex items-center justify-center rounded-md bg-ink-900 text-parchment-50 px-5 py-2.5 text-sm font-semibold hover:bg-ink-800 disabled:opacity-60"
        >
          {isSubmitting ? 'Sending…' : 'Send reset link'}
        </button>

        <p className="text-center text-sm">
          <Link to="/login" className="text-gold-700 underline">
            Back to sign in
          </Link>
        </p>
      </form>
    </AuthShell>
  );
}
